/**
 * Section: videos-carousel V12 (HLS)
 * Le Sensier — Carrossel coverflow de videos verticais 9:16 estilo Stories.
 *
 * V12 changes (reforma de entrega adaptativa):
 * - Markup vem SEM src/source (video-product-card.liquid). A atribuicao de src e ato
 *   EXCLUSIVO deste JS — isso neutraliza os preload-flips do tema Prestige
 *   (VideoMedia inView 800px + EffectCarousel preloadImages), que viram no-ops de rede.
 * - Entrega adaptativa em 3 vias por video:
 *     1. HLS nativo (Safari/iOS, incl. Chrome iOS = WebKit): video.src = .m3u8
 *     2. hls.js light (Chrome/Firefox/Edge/Android): MSE + ABR, capLevelToPlayerSize
 *     3. Fallback MP4 progressivo (status quo antigo, nunca pior)
 * - Tiers por ATTACH de stream (nao mais por atributo preload):
 *     center (delta 0)    -> attach + startLoad + play
 *     vizinho (delta ±1)  -> attach so manifest (autoStartLoad: false)
 *     resto               -> detach total (destroy + removeAttribute('src') + load())
 *   Pool maximo de 3 streams vivos. Nunca 11 downloads simultaneos.
 * - hls-light.min.js e lazy: injetado apenas quando a section entra no viewport
 *   (IntersectionObserver ja existente), promise cacheada, timeout 4s -> fallback MP4.
 * - Loading UI: data-loading no slide central quando readyState<2 (delay 180ms para
 *   nao piscar no fast path; re-arma em 'waiting' com debounce 300ms).
 * - Guards: prefers-reduced-motion -> nenhum stream (poster only);
 *   Save-Data -> so atacha apos primeira interacao do usuario.
 *
 * Eventos do EffectCarousel reaproveitados:
 * - carousel:change -> _updatePositions + _retierStreams
 * - carousel:settle -> _updatePositions + _syncPlayback (toca/pausa videos)
 */

(function () {
  'use strict';

  const EffectCarouselCtor = window.customElements?.get?.('effect-carousel');

  if (!EffectCarouselCtor) {
    console.warn('[videos-carousel] EffectCarousel base nao encontrado. Carrossel rodara em fallback statico.');
    return;
  }

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
  const saveData = navigator.connection?.saveData === true;

  // ---------------------------------------------------------------------------
  // Loader compartilhado do hls.js light (uma promise por pagina, com timeout)
  // ---------------------------------------------------------------------------
  let hlsLibPromise = null;

  function loadHlsLib(src) {
    if (window.Hls) {
      return Promise.resolve(window.Hls.isSupported?.() ? window.Hls : null);
    }
    if (hlsLibPromise) return hlsLibPromise;
    if (!src) return Promise.resolve(null);

    hlsLibPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      const timer = setTimeout(() => resolve(null), 8000);
      script.src = src;
      script.async = true;
      script.onload = () => { clearTimeout(timer); resolve(window.Hls || null); };
      script.onerror = () => { clearTimeout(timer); resolve(null); };
      document.head.appendChild(script);
    }).then((Hls) => (Hls && Hls.isSupported?.() ? Hls : null));

    return hlsLibPromise;
  }

  // Fallback permanente: deriva a URL do master .m3u8 a partir da URL do MP4 do CDN
  // Shopify (padrao confirmado: .../<arquivo>.HD-1080p-7.2Mbps-<id>.mp4 -> .../<arquivo>.m3u8)
  function deriveHlsUrl(mp4Url) {
    if (!mp4Url) return '';
    return mp4Url.replace(/\.[^.\/]+\.mp4(\?.*)?$/, '.m3u8');
  }

  class VideosCarousel extends EffectCarouselCtor {
    connectedCallback() {
      super.connectedCallback?.();

      this._onChange = this._onChange.bind(this);
      this._onSettle = this._onSettle.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this.addEventListener('carousel:change', this._onChange);
      this.addEventListener('carousel:settle', this._onSettle);

      this.addEventListener('click', this._onClick);
      this.addEventListener('pointerdown', this._onPointerDown);

      this._dragState = { active: false, startX: 0, startY: 0, deltaX: 0, locked: null, pointerId: null };
      this._initialized = false;
      this._userEngaged = false;
      // Map<HTMLVideoElement, { mode, hls, tier, ready, cancelled }>
      this._streams = new Map();

      // V11 fix #3 (FOUC): posicionar slides + revelar a section imediatamente (so layout, sem tocar video).
      requestAnimationFrame(() => {
        this._updatePositions();
        this.classList.add('is-ready');
      });

      // V11 fix #4 (perf): lazy-init do PLAYBACK — so atacha/toca stream quando a section
      // entra no viewport. Antes disso nenhum <video> tem src (markup V12) = zero rede.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting) && !this._initialized) {
            this._initialized = true;
            this._io.disconnect();
            this._io = null;
            this._initPlayback();
          }
        }, { rootMargin: '300px' });
        this._io.observe(this);
      } else {
        this._initialized = true;
        requestAnimationFrame(() => this._initPlayback());
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.removeEventListener('carousel:change', this._onChange);
      this.removeEventListener('carousel:settle', this._onSettle);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('pointerdown', this._onPointerDown);
      this._io?.disconnect();
      this._pauseAll();
      // Teardown total dos streams (libera MSE/buffers)
      Array.from(this._streams.keys()).forEach((video) => this._detachStream(video));
    }

    // Sobrescreve animation do EffectCarousel (fade default). Coverflow e via CSS variable --vc-offset,
    // entao a animation "oficial" e noop.
    createOnChangeAnimationControls(fromSlide, toSlide) {
      const noop = { finished: Promise.resolve(), finish: () => {} };
      return noop;
    }

    createOnBecameVisibleAnimationControls(toSlide) {
      return { finished: Promise.resolve(), finish: () => {} };
    }

    _initPlayback() {
      // Warm-up da lib em paralelo com o attach do primeiro tier (promise cacheada)
      if (!prefersReducedMotion && !saveData) {
        loadHlsLib(this.dataset.hlsLib);
      }
      this._retierStreams();
      this._syncPlayback({ initial: true });
    }

    _onChange() {
      this._updatePositions();
      this._retierStreams();
    }

    _onSettle() {
      this._updatePositions();
      this._syncPlayback();
    }

    _updatePositions() {
      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      cells.forEach((cell, i) => {
        let delta = i - selectedIdx;
        // wraparound cyclic — sempre escolher caminho mais curto pelo anel
        if (delta > total / 2) delta -= total;
        else if (delta < -total / 2) delta += total;

        cell.style.setProperty('--vc-offset', delta);

        // V8: 5 niveis (center / neighbor / far / very-far / hidden) = 7 cards visiveis em desktop
        const absDelta = Math.abs(delta);
        let position;
        if (delta === 0) position = 'center';
        else if (absDelta === 1) position = 'neighbor';
        else if (absDelta === 2) position = 'far';
        else if (absDelta === 3) position = 'very-far';
        else position = 'hidden';
        cell.dataset.position = position;

        // V8: data-side controla sinal do translate via CSS (--vc-side: -1 esq, 1 dir, 0 center)
        cell.dataset.side = delta < 0 ? 'left' : (delta > 0 ? 'right' : 'center');

        cell.classList.toggle('is-selected', delta === 0);
      });
    }

    // -------------------------------------------------------------------------
    // V12 — GERENCIADOR DE STREAMS (substitui o antigo _preloadNeighbors)
    // -------------------------------------------------------------------------

    /**
     * Recalcula o tier de cada video e atacha/detacha streams de acordo.
     * center (0) -> attach + load | vizinho (1) -> attach so manifest | resto -> detach.
     */
    _retierStreams() {
      if (!this._initialized) return;
      if (prefersReducedMotion) return; // poster only — nenhum stream
      if (saveData && !this._userEngaged) return; // Save-Data: so apos interacao

      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      cells.forEach((cell, i) => {
        const video = cell.querySelector('video');
        if (!video) return;

        let delta = i - selectedIdx;
        if (delta > total / 2) delta -= total;
        else if (delta < -total / 2) delta += total;
        const absDelta = Math.abs(delta);

        if (absDelta <= 1) {
          this._ensureStream(video, absDelta);
        } else {
          this._detachStream(video);
        }
      });
    }

    /**
     * Garante stream atachado no video com o tier dado (0 = center, 1 = vizinho).
     * Idempotente: re-chamadas so re-aplicam o tier (startLoad/stopLoad).
     */
    _ensureStream(video, tier) {
      let entry = this._streams.get(video);
      if (!entry) {
        entry = {
          mode: null, hls: null, tier: tier, ready: null, cancelled: false,
          url: '', sourceLoaded: false, parsed: false, started: false
        };
        this._streams.set(video, entry);
        this._bindLoadingUI(video);
        entry.ready = this._createStream(video, entry).catch((err) => {
          if (window.Shopify?.designMode) console.debug('[videos-carousel] attach falhou:', err);
        });
      }
      entry.tier = tier;
      entry.cancelled = false;
      entry.ready.then(() => this._applyTier(video, entry));
      return entry;
    }

    /**
     * Ordem deterministica, sem race com o manifest em voo:
     * - loadSource so acontece AQUI (apos attachMedia, dentro do ready)
     * - startLoad (fragmentos) so apos MANIFEST_PARSED (hook registrado no _createStream)
     * - stopLoad so ao REBAIXAR um center que de fato iniciou (started) — nunca no attach
     *   inicial do vizinho (stopLoad em manifest em voo cancela o XHR e mata a instancia)
     */
    _applyTier(video, entry) {
      if (entry.cancelled) return;
      if (entry.mode === 'hls' && entry.hls) {
        if (!entry.sourceLoaded) {
          entry.sourceLoaded = true;
          entry.hls.loadSource(entry.url);
        }
        if (entry.tier === 0) {
          if (entry.parsed && !entry.started) {
            entry.started = true;
            entry.hls.startLoad();
          }
          // se ainda nao parseou, o hook de MANIFEST_PARSED chama startLoad quando chegar
        } else if (entry.started) {
          entry.hls.stopLoad(); // mantem instancia + buffer; voltar ao centro e instantaneo
          entry.started = false;
        }
      } else if (entry.mode === 'native' || entry.mode === 'mp4') {
        video.preload = entry.tier === 0 ? 'auto' : 'metadata';
      }
    }

    async _createStream(video, entry) {
      const mp4Url = video.dataset.mp4Src || '';
      const hlsUrl = video.dataset.hlsSrc || deriveHlsUrl(mp4Url);

      if (!video.dataset.hlsSrc && window.Shopify?.designMode) {
        console.debug('[videos-carousel] data-hls-src ausente; usando URL derivada:', hlsUrl);
      }

      // Via 1: hls.js (MSE + ABR) — PREFERIDO sempre que suportado, mesmo onde o browser
      // reporta HLS "nativo": Chromium forks (ex.: Comet/Perplexity) respondem 'maybe' ao
      // canPlayType de HLS sem saber tocar — confiar neles = card morto (validado 2026-07-20).
      // Safari macOS e iOS 17.1+ (ManagedMediaSource) tambem rodam hls.js sem problema.
      if (hlsUrl) {
        const Hls = await loadHlsLib(this.dataset.hlsLib);
        if (entry.cancelled) return;
        if (Hls) {
          const hls = new Hls({
            capLevelToPlayerSize: true, // card ~240-280px -> ABR trava na rendition 480p
            autoStartLoad: false,       // NOS decidimos quando baixar segmento (vizinho = so manifest)
            maxBufferLength: 30,        // clipes de 6-20s -> center bufferiza inteiro
            backBufferLength: 30        // inicio permanece no buffer -> loop sem rebuffer/flash
          });
          entry.mode = 'hls';
          entry.hls = hls;
          entry.url = hlsUrl;

          // startLoad (fragmentos) so quando o manifest estiver parseado — evita race
          // startLoad/stopLoad x manifest em voo (matava a instancia silenciosamente).
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            entry.parsed = true;
            if (entry.tier === 0 && !entry.cancelled && !entry.started) {
              entry.started = true;
              hls.startLoad();
            }
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (!data || !data.fatal) return;
            // Erro fatal -> destroy + fallback MP4 naquele video (nunca card morto)
            try { hls.destroy(); } catch (e) {}
            entry.hls = null;
            entry.mode = 'mp4';
            if (mp4Url && !entry.cancelled) {
              video.src = mp4Url;
              video.preload = entry.tier === 0 ? 'auto' : 'metadata';
              if (entry.tier === 0 && video.paused) {
                const p = video.play();
                if (p && typeof p.catch === 'function') p.catch(() => {});
              }
            }
          });

          // So attachMedia aqui; loadSource acontece no _applyTier (pos-ready), numa
          // unica ordem canonica: attach -> loadSource -> (parsed) -> startLoad.
          hls.attachMedia(video);
          return;
        }
      }

      // Via 2: HLS nativo — so quando MSE/hls.js indisponivel (iOS antigo, lib falhou no load).
      // iPhone pre-iOS 17.1 nao tem MSE -> Hls.isSupported() false -> cai aqui (correto).
      if (hlsUrl && video.canPlayType('application/vnd.apple.mpegurl') !== '') {
        entry.mode = 'native';
        video.src = hlsUrl;
        // Watchdog anti-stream-morto: Chromiums mentem 'maybe' no canPlayType de HLS.
        // Se em 8s o video nao saiu de readyState 0, o "nativo" era mentira -> MP4.
        setTimeout(() => {
          if (entry.cancelled || entry.mode !== 'native') return;
          if (video.readyState === 0 && mp4Url) {
            entry.mode = 'mp4';
            video.src = mp4Url;
            video.preload = entry.tier === 0 ? 'auto' : 'metadata';
            if (entry.tier === 0) {
              const p = video.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            }
          }
        }, 8000);
        return;
      }

      // Via 3: fallback MP4 progressivo (status quo antigo)
      entry.mode = 'mp4';
      if (mp4Url) video.src = mp4Url;
    }

    _detachStream(video) {
      const entry = this._streams.get(video);
      if (!entry) return;
      entry.cancelled = true;
      this._streams.delete(video);

      const teardown = () => {
        try { entry.hls?.destroy(); } catch (e) {}
        entry.hls = null;
        try { video.pause(); } catch (e) {}
        if (video.getAttribute('src') !== null) {
          video.removeAttribute('src');
          try { video.load(); } catch (e) {} // libera buffer/decoder
        }
        video.closest('.videos-carousel__slide')?.removeAttribute('data-loading');
      };

      if (entry.ready) entry.ready.then(teardown);
      else teardown();
    }

    // -------------------------------------------------------------------------
    // V12 — LOADING UI (spinner discreto sobre o poster do slide central)
    // -------------------------------------------------------------------------

    _bindLoadingUI(video) {
      if (video._vcLoadingBound) return;
      video._vcLoadingBound = true;

      const clearLoading = () => {
        clearTimeout(video._vcWaitTimer);
        clearTimeout(video._vcArmTimer);
        video.closest('.videos-carousel__slide')?.removeAttribute('data-loading');
      };

      video.addEventListener('playing', clearLoading);
      video.addEventListener('canplay', clearLoading);

      // Re-arma em stall no meio da reproducao (debounce 300ms pra ignorar micro-stalls)
      video.addEventListener('waiting', () => {
        clearTimeout(video._vcWaitTimer);
        video._vcWaitTimer = setTimeout(() => {
          const cell = video.closest('.videos-carousel__slide');
          if (cell && cell.classList.contains('is-selected')) {
            cell.setAttribute('data-loading', '');
          }
        }, 300);
      });
    }

    _armLoadingUI(video) {
      clearTimeout(video._vcArmTimer);
      if (video.readyState >= 2) return; // fast path: nunca mostra spinner
      video._vcArmTimer = setTimeout(() => {
        const cell = video.closest('.videos-carousel__slide');
        if (cell && cell.classList.contains('is-selected') && video.readyState < 2) {
          cell.setAttribute('data-loading', '');
        }
      }, 180);
    }

    // -------------------------------------------------------------------------
    // PLAYBACK
    // -------------------------------------------------------------------------

    _syncPlayback(opts = {}) {
      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      cells.forEach((cell, i) => {
        const video = cell.querySelector('video');
        const isCenter = i === selectedIdx;

        if (prefersReducedMotion) {
          cell.setAttribute('data-reduced-motion', '');
          if (video) {
            try { video.pause(); } catch (e) {}
          }
          return;
        }

        if (!video) return;

        if (isCenter) {
          // V11 fix #2 (pisca preto): loop NATIVO do HTML5 re-adicionado em runtime
          // (markup V12 vem sem loop — com loop o Shopify omitiria o HLS no video_tag antigo;
          // no render manual mantemos a mesma convencao: loop e responsabilidade do JS).
          if (!video.hasAttribute('loop')) {
            video.setAttribute('loop', '');
          }
          if (video._vcLoopHandler) {
            video.removeEventListener('ended', video._vcLoopHandler);
            video._vcLoopHandler = null;
          }

          if (saveData && !this._userEngaged) {
            // Save-Data: poster only ate a primeira interacao (tap) do usuario
            return;
          }

          // V12: garante stream atachado ANTES do play (async na primeira vez)
          const entry = this._ensureStream(video, 0);
          this._armLoadingUI(video);

          const doPlay = () => {
            if (entry.cancelled) return;

            if (!opts.initial && video.readyState >= 1) {
              try { video.currentTime = 0; } catch (e) {}
            }

            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise
                .then(() => {
                  cell.removeAttribute('data-suspended');
                })
                .catch((err) => {
                  if (err && err.name === 'NotAllowedError') {
                    cell.setAttribute('data-suspended', '');
                  } else if (window.Shopify?.designMode) {
                    console.debug('[videos-carousel] play() falhou:', err);
                  }
                });
            }
          };

          if (entry.ready) entry.ready.then(doPlay);
          else doPlay();

          // Sincroniza UI do mute toggle com o estado real (so o center tem toggle visivel)
          this._syncMuteToggleUI(cell, video.muted);
        } else {
          try {
            video.pause();
            // V7: reset currentTime libera buffer/CPU dos vizinhos
            if (video.currentTime > 0.1) video.currentTime = 0;
          } catch (e) {}

          cell.removeAttribute('data-loading');
          clearTimeout(video._vcArmTimer);

          if (video._vcLoopHandler) {
            video.removeEventListener('ended', video._vcLoopHandler);
            video._vcLoopHandler = null;
          }

          // V11 fix #1 (audio pisca): resetar muted SEM tocar no UI do toggle.
          if (video.muted === false) {
            video.muted = true;
          }
        }
      });
    }

    _syncMuteToggleUI(cell, isMuted) {
      const toggle = cell.querySelector('.videos-carousel__play-toggle');
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(isMuted));
      toggle.setAttribute('aria-label', isMuted ? 'Ativar som do vídeo' : 'Silenciar vídeo');
      const mutedIcon = toggle.querySelector('.videos-carousel__play-icon--muted');
      const unmutedIcon = toggle.querySelector('.videos-carousel__play-icon--unmuted');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }

    _pauseAll() {
      (this.cells || []).forEach((cell) => {
        const video = cell.querySelector('video');
        if (video) {
          try { video.pause(); } catch (e) {}
        }
      });
    }

    _markEngaged() {
      if (this._userEngaged) return;
      this._userEngaged = true;
      // Save-Data: primeira interacao libera o attach do tier atual
      if (saveData) {
        this._retierStreams();
        this._syncPlayback();
      }
    }

    // CLICK HANDLING — V3: separa play-toggle / product-card / video-trigger
    _onClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      this._markEngaged();

      // 1. Play/mute toggle — sempre prevalece (z-index alto)
      const toggle = e.target.closest('.videos-carousel__play-toggle');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        this._handleMuteToggle(toggle);
        return;
      }

      // 2. Product card — deixa <a href> fazer o trabalho (NAO interceptar)
      if (e.target.closest('.videos-carousel__product-card')) return;

      // 3. Video trigger
      const trigger = e.target.closest('.videos-carousel__video-trigger');
      if (!trigger) return;

      // Se o drag estava ativo, ignora (era drag, nao click)
      if (this._dragState.consumed) {
        this._dragState.consumed = false;
        return;
      }

      e.preventDefault();
      const slide = trigger.closest('.videos-carousel__slide');
      if (!slide) return;
      const index = parseInt(slide.dataset.slideIndex, 10) || 0;

      // V3 DEFAULT: click no video seleciona o slide (carrossel anima ate ele)
      // V2 LEGACY: se enable_fullscreen=true, dispara open do viewer
      const enableFullscreen = this.dataset.enableFullscreen === 'true';

      if (enableFullscreen) {
        const sectionId = this.dataset.sectionId || this.id;
        document.dispatchEvent(new CustomEvent('videos-fullscreen:open', {
          bubbles: true,
          detail: { sectionId: sectionId, startIndex: index, sourceCarousel: this }
        }));
      } else {
        // Se o slide ja eh o central, click ativa som (toggle mute)
        if (index === this.selectedIndex) {
          const video = slide.querySelector('video');
          if (video) {
            video.muted = !video.muted;
            this._syncMuteToggleUI(slide, video.muted);
          }
          return;
        }
        // Caso contrario, animar carrossel ate o slide clicado
        this.select?.(index);
      }
    }

    _handleMuteToggle(toggleBtn) {
      const slide = toggleBtn.closest('.videos-carousel__slide');
      if (!slide) return;
      const video = slide.querySelector('video');
      if (!video) return;
      // So permite toggle no slide central
      if (slide.dataset.position !== 'center') return;
      video.muted = !video.muted;
      this._syncMuteToggleUI(slide, video.muted);
    }

    // DRAG/SWIPE HORIZONTAL — pointerdown/move/up custom
    _onPointerDown(e) {
      this._markEngaged();

      // Nao iniciar drag em clicks em links/buttons explicitos do product-card
      if (e.target.closest('.videos-carousel__product-card')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this._dragState.active = true;
      this._dragState.locked = null; // ainda nao decidiu eixo
      this._dragState.startX = e.clientX;
      this._dragState.startY = e.clientY;
      this._dragState.deltaX = 0;
      this._dragState.pointerId = e.pointerId;
      this._dragState.consumed = false;

      this.addEventListener('pointermove', this._onPointerMove);
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._dragState.active) return;
      if (e.pointerId !== this._dragState.pointerId) return;

      const dx = e.clientX - this._dragState.startX;
      const dy = e.clientY - this._dragState.startY;

      // Lock no eixo dominante apos 6px de movimento
      if (this._dragState.locked === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        this._dragState.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this._dragState.locked === 'y') {
          // Movimento vertical: cancelar (usuario scrolla a pagina)
          this._dragState.active = false;
          this.removeEventListener('pointermove', this._onPointerMove);
          this.removeEventListener('pointerup', this._onPointerUp);
          this.removeEventListener('pointercancel', this._onPointerUp);
          return;
        }
        // Lock em X: capturar pointer
        try { this.setPointerCapture(this._dragState.pointerId); } catch (err) {}
      }

      if (this._dragState.locked !== 'x') return;

      this._dragState.deltaX = dx;

      // Visual feedback: aplicar offset temporario.
      // V8: usa --vc-step-1 (distancia center->neighbor) como unidade do drag factor.
      const cellWidth = this._getEffectiveCellWidth();
      const stepFromVar = parseFloat(getComputedStyle(this).getPropertyValue('--vc-step-1'));
      const stepWidth = (stepFromVar && !isNaN(stepFromVar))
        ? stepFromVar
        : (cellWidth + (parseFloat(getComputedStyle(this).getPropertyValue('--vc-gap')) || 16));
      const dragFactor = dx / (stepWidth || 1);
      this.style.setProperty('--vc-drag', dragFactor.toFixed(3));

      // Marca o carrossel como em-drag (CSS desativa transitions)
      this.dataset.dragging = 'true';

      // V4: threshold 8px (era 4) + so marcar consumed se realmente em drag horizontal.
      // Evita bloquear click subsequente por jitter de mouse.
      if (Math.abs(dx) > 8 && this._dragState.locked === 'x') {
        this._dragState.consumed = true; // bloqueia click subsequente
      }

      // Cancela selecao de texto durante drag
      if (e.cancelable) e.preventDefault();
    }

    _onPointerUp(e) {
      if (!this._dragState.active && this._dragState.locked !== 'x') {
        this._cleanupDrag();
        return;
      }

      const dx = this._dragState.deltaX;
      const cellWidth = this._getEffectiveCellWidth();
      const threshold = cellWidth * 0.18; // ~18% do card pra trigger navegacao

      this.style.removeProperty('--vc-drag');
      delete this.dataset.dragging;

      if (Math.abs(dx) > threshold) {
        if (dx < 0) this.next?.();
        else this.previous?.();
      }

      this._cleanupDrag();
    }

    _cleanupDrag() {
      try { if (this._dragState.pointerId != null) this.releasePointerCapture(this._dragState.pointerId); } catch (err) {}
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerup', this._onPointerUp);
      this.removeEventListener('pointercancel', this._onPointerUp);

      this._dragState.active = false;
      this._dragState.locked = null;
      this._dragState.deltaX = 0;
      this._dragState.pointerId = null;

      // V4: consumed deve ser resetado por _onClick (que bloqueia 1 click apos drag).
      // Mas se o click event nao chegou (ex: mouseup fora do elemento), agendamos reset
      // defensivo no proximo tick — garante que clicks futuros nao fiquem bloqueados.
      if (this._dragState.consumed) {
        setTimeout(() => { this._dragState.consumed = false; }, 50);
      }
    }

    _getEffectiveCellWidth() {
      const first = this.cells?.[0];
      if (!first) return 320;
      const rect = first.getBoundingClientRect();
      return rect.width || 320;
    }
  }

  if (!window.customElements.get('videos-carousel')) {
    window.customElements.define('videos-carousel', VideosCarousel);
  }
})();
